import React from 'react';
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon, RefreshCwIcon } from 'lucide-react';
export type TaxCodeLifecycleHistory = {
  id: string;
  taxCodeId: string;
  action: 'Activated' | 'Deactivated' | 'Reactivated';
  activeFrom: string;
  activeTo: string | null;
  effectiveDate: string;
  reason?: string;
  performedBy: string;
  performedAt: string;
};
interface TaxCodeLifecycleHistoryProps {
  taxCode: any;
  lifecycleHistory: TaxCodeLifecycleHistory[];
  onBack: () => void;
  locale?: string;
}
export function TaxCodeLifecycleHistory({
  taxCode,
  lifecycleHistory,
  onBack,
  locale = 'en-US'
}: TaxCodeLifecycleHistoryProps) {
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'Activated':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" data-id="element-12065" />;
      case 'Deactivated':
        return <XCircleIcon className="h-5 w-5 text-red-500" data-id="element-12066" />;
      case 'Reactivated':
        return <RefreshCwIcon className="h-5 w-5 text-blue-500" data-id="element-12067" />;
      default:
        return null;
    }
  };
  const getActionColor = (action: string) => {
    switch (action) {
      case 'Activated':
        return 'bg-green-100 text-green-800';
      case 'Deactivated':
        return 'bg-red-100 text-red-800';
      case 'Reactivated':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-12068">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-12069">
        <div className="flex items-center justify-between" data-id="element-12070">
          <div data-id="element-12071">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12072">
              Lifecycle History: {taxCode.code}
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-12073">
              {taxCode.description}
            </p>
          </div>
          <button onClick={onBack} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-12074">
            <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-12075" />
            Back
          </button>
        </div>
      </div>

      <div className="px-4 py-5 sm:p-6" data-id="element-12076">
        {/* Current Status */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg" data-id="element-12077">
          <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="element-12078">
            Current Status
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-12079">
            <div data-id="element-12080">
              <p className="text-xs text-gray-500" data-id="element-12081">Status</p>
              <p className="mt-1" data-id="element-12082">
                {taxCode.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-12083">
                    <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-12084" />
                    Active
                  </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-12085">
                    <XCircleIcon className="h-3 w-3 mr-1" data-id="element-12086" />
                    Inactive
                  </span>}
              </p>
            </div>
            <div data-id="element-12087">
              <p className="text-xs text-gray-500" data-id="element-12088">Active From</p>
              <p className="mt-1 text-sm font-medium text-gray-900" data-id="element-12089">
                {new Date(taxCode.activeFrom).toLocaleDateString(locale)}
              </p>
            </div>
            <div data-id="element-12090">
              <p className="text-xs text-gray-500" data-id="element-12091">Active To</p>
              <p className="mt-1 text-sm font-medium text-gray-900" data-id="element-12092">
                {taxCode.activeTo ? new Date(taxCode.activeTo).toLocaleDateString(locale) : '—'}
              </p>
            </div>
          </div>
        </div>

        {/* Lifecycle History Timeline */}
        <div data-id="element-12093">
          <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-12094">
            Lifecycle Events
          </h4>

          {lifecycleHistory.length === 0 ? <div className="text-center py-12" data-id="element-12095">
              <p className="text-sm text-gray-500" data-id="element-12096">
                No lifecycle history available
              </p>
            </div> : <div className="flow-root" data-id="element-12097">
              <ul className="-mb-8" data-id="element-12098">
                {lifecycleHistory.sort((a, b) => new Date(b.performedAt).getTime() - new Date(a.performedAt).getTime()).map((entry, entryIdx) => <li key={entry.id} data-id="element-12099">
                      <div className="relative pb-8" data-id="element-12100">
                        {entryIdx !== lifecycleHistory.length - 1 ? <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" data-id="element-12101" /> : null}
                        <div className="relative flex space-x-3" data-id="element-12102">
                          <div data-id="element-12103">
                            <span className="h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white bg-white" data-id="element-12104">
                              {getActionIcon(entry.action)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-12105">
                            <div className="flex items-center justify-between" data-id="element-12106">
                              <div data-id="element-12107">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(entry.action)}`} data-id="element-12108">
                                  {entry.action}
                                </span>
                              </div>
                              <div className="text-right text-sm whitespace-nowrap text-gray-500" data-id="element-12109">
                                {new Date(entry.performedAt).toLocaleString(locale)}
                              </div>
                            </div>
                            <div className="mt-2 text-sm text-gray-700" data-id="element-12110">
                              <div className="grid grid-cols-2 gap-2" data-id="element-12111">
                                <div data-id="element-12112">
                                  <span className="text-gray-500" data-id="element-12113">
                                    Active From:
                                  </span>{' '}
                                  <span className="font-medium" data-id="element-12114">
                                    {new Date(entry.activeFrom).toLocaleDateString(locale)}
                                  </span>
                                </div>
                                <div data-id="element-12115">
                                  <span className="text-gray-500" data-id="element-12116">
                                    Active To:
                                  </span>{' '}
                                  <span className="font-medium" data-id="element-12117">
                                    {entry.activeTo ? new Date(entry.activeTo).toLocaleDateString(locale) : '—'}
                                  </span>
                                </div>
                              </div>
                              {entry.reason && <div className="mt-2" data-id="element-12118">
                                  <span className="text-gray-500" data-id="element-12119">Reason:</span>{' '}
                                  <span className="italic" data-id="element-12120">{entry.reason}</span>
                                </div>}
                              <div className="mt-1 text-xs text-gray-500" data-id="element-12121">
                                Performed by {entry.performedBy}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>)}
              </ul>
            </div>}
        </div>
      </div>
    </div>;
}